import classNames from 'classnames'
import React, {
  CSSProperties,
  ChangeEvent,
  forwardRef,
  memo,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { useDynamicTextArea } from '@global/formField/hooks/useDynamicTextArea'

import { BaseField, BaseFieldProps, LabelConstraint } from '../BaseField/BaseField'

import styles from './Textarea.module.scss'

type SharedBaseFieldKeys =
  | 'labelSupportText'
  | 'hint'
  | 'hintSupportText'
  | 'hintIcon'
  | 'leftIcon'
  | 'rightText'
  | 'rightIcon'
  | 'rightContent'
  | 'disabled'
  | 'error'
  | 'readOnly'
  | 'size'
  | 'fullWidth'

export type TextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size' | 'placeholder' | 'className' | 'style'
> &
  Pick<BaseFieldProps, SharedBaseFieldKeys> &
  LabelConstraint & {
    dynamicHeight?: boolean
    resize?: CSSProperties['resize']
    maxHeight?: CSSProperties['maxHeight']
  }

export const Textarea = memo(
  forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
    const {
      label,
      size = 'xl',
      labelSupportText,
      hint,
      hintSupportText,
      placeholder = label,
      hintIcon,
      disabled,
      error,
      readOnly,
      dynamicHeight = false,
      fullWidth,
      value,
      onChange,
      onFocus,
      onBlur,
      name,
      required,
      autoComplete,
      rows,
      resize,
      maxHeight,
      ...textareaProps
    } = props

    const dynamicHeightRef = useDynamicTextArea()
    const genericRef = useRef<HTMLTextAreaElement>(null)

    const activeRef = dynamicHeight ? dynamicHeightRef : genericRef

    useImperativeHandle(ref, () => activeRef.current as HTMLTextAreaElement, [activeRef])

    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState(() => {
      return !!(value !== undefined ? value : textareaProps.defaultValue)
    })

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (value === undefined) {
        setHasValue(!!e.target.value)
      }

      if (onChange) {
        onChange(e)
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true)
      if (onFocus) onFocus(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false)

      if (e.target.value) {
        const trimmed = e.target.value.trim()

        if (trimmed !== e.target.value) {
          e.target.value = trimmed
          onChange?.(e as unknown as ChangeEvent<HTMLTextAreaElement>)
        }
      }

      if (onBlur) {
        onBlur(e)
      }
    }

    const isControlled = value !== undefined
    const actuallyHasValue = isControlled ? !!value : hasValue

    const isPlaceholderMatchingLabel =
      !!label && !!placeholder && placeholder.trim() === label.trim()
    const useFloatingLabel = !actuallyHasValue && !isFocused && isPlaceholderMatchingLabel

    const resolvedPlaceholder = isPlaceholderMatchingLabel ? ' ' : placeholder

    return (
      <BaseField
        variant='textarea'
        label={label}
        isLabelActingAsPlaceholder={useFloatingLabel}
        labelSupportText={labelSupportText}
        hint={hint}
        hintSupportText={hintSupportText}
        hintIcon={hintIcon}
        disabled={disabled}
        error={error}
        readOnly={readOnly}
        size={size}
        fullWidth={fullWidth}
      >
        <textarea
          {...textareaProps}
          name={name}
          placeholder={resolvedPlaceholder}
          data-placeholder={placeholder}
          ref={activeRef}
          className={classNames(styles.textarea, {
            [styles.dynamicHeight]: dynamicHeight,
          })}
          style={{ resize, maxHeight }}
          rows={rows}
          required={required}
          autoComplete={autoComplete}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          disabled={disabled}
          readOnly={readOnly}
          onBlur={handleBlur}
        />
      </BaseField>
    )
  })
)
